import { forwardRef } from "react";

import { DateTime } from "luxon";

import { SimpleFormDateRange } from "./SimpleFormDateRange";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useTranslate } from "@/common/hooks/useTranslate";
import { BaseFormDate, BaseFormDateProps } from "@/forms-design-system/FormDate/BaseFormDate";
import {
  formatTimeWithTimezone,
  getDateInTimezone,
  isMultiDayDate,
  shouldDisplayYear,
} from "@/forms-design-system/FormDate/helper";

export type FormDateRangeWithScarcityProps = Omit<BaseFormDateProps, "date" | "title" | "subtitle"> & {
  startDate: Date;
  endDate: Date;
  now?: Date; // If not provided, the current date will be used
  timezone?: string | null;
};

export const FormDateRangeWithScarcity = forwardRef<HTMLDivElement, FormDateRangeWithScarcityProps>(
  ({ startDate, endDate, now, timezone, ...rest }, ref) => {
    const { t } = useTranslate();
    const { isoLocale } = useLocaleContext();
    const luxonNow = now ? getDateInTimezone(now, timezone) : DateTime.now().setZone(timezone || undefined);
    const luxonStartDate = getDateInTimezone(startDate, timezone);
    const luxonEndDate = getDateInTimezone(endDate, timezone);
    const multiDay = isMultiDayDate(luxonStartDate, luxonEndDate);

    const daysRemaining = Math.floor(luxonStartDate.diff(luxonNow, ["days"]).days);

    const isImminent = daysRemaining <= 7 && daysRemaining > 0;

    if (!isImminent) {
      return <SimpleFormDateRange startDate={startDate} endDate={endDate} now={now} timezone={timezone} {...rest} />;
    }

    const startTimeDisplay = luxonStartDate.toLocaleString(
      { hour: "numeric", minute: "2-digit" },
      { locale: isoLocale }
    );

    const endTimeDisplay = formatTimeWithTimezone(luxonEndDate, isoLocale);

    if (multiDay) {
      return (
        <BaseFormDate
          date={startDate}
          title={t("common", "daysRemaining", {
            count: daysRemaining,
          })}
          subtitle={t("common", "compactMultiDayDateRange", {
            startDate: luxonStartDate.toLocaleString(
              {
                year: shouldDisplayYear(luxonStartDate, luxonNow) ? "numeric" : undefined,
                month: "short",
                day: "numeric",
              },
              { locale: isoLocale }
            ),
            startTime: startTimeDisplay,
            endDate: luxonEndDate.toLocaleString(
              {
                year: shouldDisplayYear(luxonEndDate, luxonNow) ? "numeric" : undefined,
                month: "short",
                day: "numeric",
              },
              { locale: isoLocale }
            ),
            endTime: endTimeDisplay,
          })}
          {...rest}
          ref={ref}
        />
      );
    }

    return (
      <BaseFormDate
        date={startDate}
        title={t("common", "daysRemaining", { count: daysRemaining })}
        subtitle={t("common", "compactSingleDayDateRange", {
          startDate: luxonStartDate.toLocaleString(
            {
              year: shouldDisplayYear(luxonStartDate, luxonNow) ? "numeric" : undefined,
              month: "short",
              day: "numeric",
            },
            { locale: isoLocale }
          ),
          startTime: startTimeDisplay,
          endTime: endTimeDisplay,
        })}
        {...rest}
        ref={ref}
      />
    );
  }
);

FormDateRangeWithScarcity.displayName = "FormDateRangeWithScarcity";
